import React__default, { useContext, useRef, useState, useMemo, useEffect } from 'react';
import cn from 'classnames';
import { useTranslate } from '@awardit/react-use-translate';
import useFormat from '../../helpers/use-format.js';
import { pointsPriceByID, isPointsOnly } from '../../helpers/points.js';
import { PriceSplit } from '../PriceSplit/index.js';
import { AnalyticsContext, ProductLink } from '@crossroads/analytics';
import { Square } from '../Square/index.js';
import { ProductListMedia } from '../ProductListMedia/index.js';
import { getLowestMSRP } from '../../helpers/utils.js';
import { formatConfigColors } from '../../helpers/colors.js';
import styles from 'ui-components/ProductCard.scss';

var DiscountTag = function DiscountTag(_ref) {
  var msrp = _ref.msrp,
    price = _ref.price;
  var t = useTranslate();
  var discount = typeof msrp === "number" ? Math.floor((1 - price / msrp) * 100) : 0;

  /* eslint-disable react/no-danger */
  return discount > 0 && discount < 100 ? /*#__PURE__*/React__default.createElement("p", {
    dangerouslySetInnerHTML: {
      __html: t("PRODUCT.DISCOUNT_SHORT", {
        discount: discount
      })
    },
    className: styles.discount
  }) : null;
  /* eslint-enable react/no-danger */
};

var ProductCard = function ProductCard(_ref2) {
  var product = _ref2.product,
    className = _ref2.className,
    breadcrumbLinks = _ref2.breadcrumbLinks,
    position = _ref2.position,
    list = _ref2.list,
    _ref2$pointsPriceId = _ref2.pointsPriceId,
    pointsPriceId = _ref2$pointsPriceId === void 0 ? "vanilla" : _ref2$pointsPriceId;
  var t = useTranslate();
  var gaContext = useContext(AnalyticsContext);
  var _useFormat = useFormat(),
    formatPrice = _useFormat.formatPrice;
  var gaRef = useRef(null);
  var pointsPrice = pointsPriceByID(product.pointsPrices, pointsPriceId);
  var pointsOnly = isPointsOnly(pointsPrice);
  // Default to smallest image
  var _useState = useState(""),
    imageSrc = _useState[0],
    setImageSrc = _useState[1];
  var options = product.type === "configurable" ? product.options.items : [];
  var price = product.price.incVat,
    showDiscount = product.attributes.showDiscount;
  var msrp = useMemo(function () {
    return getLowestMSRP(product);
  }, [product]);
  var mapGaObject = function mapGaObject(product) {
    var gaObject = {
      item_id: product.sku,
      // eslint-disable-line camelcase
      item_name: product.name,
      // eslint-disable-line camelcase
      item_brand: product.attributes.manufacturer,
      // eslint-disable-line camelcase
      price: product.price.incVat,
      index: Number(position) + 1,
      item_list_name: list != null ? list : "" // eslint-disable-line camelcase
    };

    if (product.categories === undefined || product.categories.length === 0) {
      return gaObject;
    }
    product.categories.forEach(function (c, i) {
      if (i === 0) {
        gaObject.item_category = c.name; // eslint-disable-line camelcase
      } else {
        gaObject["item_category" + (i + 1)] = c.name;
      }
    });
    return gaObject;
  };
  useEffect(function () {
    var gaObject = mapGaObject(product);
    if (!gaRef.current) {
      return;
    }
    gaContext.register(gaRef.current, gaObject);
  }, [gaRef]);
  return /*#__PURE__*/React__default.createElement(ProductLink, {
    className: cn(styles.productCard, className),
    list: list,
    to: {
      pathname: product.url,
      state: {
        hint: {
          type: "product",
          product: product,
          image: imageSrc
        },
        breadcrumbLinks: breadcrumbLinks,
        list: list,
        position: Number(position) + 1
      }
    },
    product: {
      name: product.name,
      sku: product.sku,
      price: product.price,
      qty: 1,
      attributes: {
        manufacturer: product.attributes.manufacturer
      },
      categories: product.categories
    },
    innerRef: gaRef,
    position: Number(position) + 1
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.hoverFG
  }), /*#__PURE__*/React__default.createElement("div", {
    className: styles.colors
  }, options.filter(function (x) {
    return Boolean(x.product.attrLabels.color);
  }).map(function (x, i) {
    var _x$product$attrLabels;
    return /*#__PURE__*/React__default.createElement("div", {
      key: i,
      style: {
        background: "linear-gradient(" + formatConfigColors((_x$product$attrLabels = x.product.attrLabels.color) == null ? void 0 : _x$product$attrLabels.title) + ")"
      }
    });
  })), /*#__PURE__*/React__default.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React__default.createElement(ProductListMedia, {
    className: styles.imageWrapper,
    product: product,
    getCurrentImageSrc: function getCurrentImageSrc(x) {
      return setImageSrc(x);
    }
  }), /*#__PURE__*/React__default.createElement("div", {
    className: styles.badges
  }, pointsOnly && /*#__PURE__*/React__default.createElement("span", null, t("PRODUCT.POINTS_ONLY")))), /*#__PURE__*/React__default.createElement("div", {
    className: styles.body
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.top
  }, /*#__PURE__*/React__default.createElement("span", {
    className: styles.name
  }, product.name), /*#__PURE__*/React__default.createElement("p", {
    className: styles.brand
  }, product.attributes.manufacturer)), /*#__PURE__*/React__default.createElement("div", {
    className: styles.bottom
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.price
  }, /*#__PURE__*/React__default.createElement(PriceSplit, {
    className: styles.price,
    pointsPrice: pointsPrice,
    price: product.price
  }), typeof msrp === "number" && msrp > product.price.incVat && showDiscount === true && /*#__PURE__*/React__default.createElement("span", {
    className: styles.msrp
  }, t("PRODUCT.MSRP"), " ", formatPrice(msrp))), showDiscount === true && /*#__PURE__*/React__default.createElement(DiscountTag, {
    price: price,
    msrp: msrp
  }))));
};
var DummyCard = function DummyCard(_ref3) {
  var className = _ref3.className;
  return /*#__PURE__*/React__default.createElement("div", {
    className: cn(className, styles.productCard, styles.dummy)
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.imageWrapper
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.image
  }, /*#__PURE__*/React__default.createElement(Square, null))), /*#__PURE__*/React__default.createElement("div", {
    className: styles.body
  }, /*#__PURE__*/React__default.createElement("p", {
    className: styles.brand
  }, "\xA0"), /*#__PURE__*/React__default.createElement("span", {
    className: styles.name
  }, "\xA0"), /*#__PURE__*/React__default.createElement("p", {
    className: styles.price
  }, "\xA0")));
};

export { DummyCard, ProductCard };
//# sourceMappingURL=index.js.map
